import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';

import { anonymousIdOptions } from 'rudder-sdk-js';

import { CONFIG } from '@arrived/config';

/**
 * https://github.com/rudderlabs/rudder-analytics-next/blob/main/sample-next-app-npm/pages/rudderInitialize.js
 * NextJS applications need this to only run on the client and not the server
 */
async function rudderInitialize() {
  const rudder = await import('rudder-sdk-js');
  // Needs to be set for rudder-sdk-js
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).rudderAnalytics = rudder;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).rudderAnalytics.load(CONFIG.rudderStack.rudderWriteKey, CONFIG.rudderStack.dataPlaneUrl, {
    integrations: { All: true }, // load call options
  });

  return new Promise<typeof rudder>((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).rudderAnalytics.ready(() => {
      resolve(rudder);
    });
  });
}

interface AnalyticsContextType {
  getAnonymousId?: (options?: anonymousIdOptions) => string | undefined;
  hasAnonymousUserLeadCaptured?: boolean;
  hasAnonymousUserLoggedIn?: boolean;
  isLoading: boolean;
  rudder?: typeof import('rudder-sdk-js');
}

export const AnalyticsContext = createContext<AnalyticsContextType>({
  isLoading: true,
});

interface AnalyticsContextProviderProps {
  children: ReactNode;
}

export const AnalyticsContextProvider = ({ children }: AnalyticsContextProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [rudder, setRudder] = useState<typeof import('rudder-sdk-js')>();

  useEffect(() => {
    rudderInitialize()
      .then((rudder) => {
        setRudder(rudder);
        console.log('Rudder Analytics successfully loaded');
      })
      .catch(() => console.log('RudderStack failed to initialize.'))
      .finally(() => setIsLoading(false));
  }, []);

  const hasAnonymousUserLoggedIn = useMemo(() => {
    if (rudder) {
      const userTraits = rudder.getUserTraits();
      // We can use all of these user traits to determine if a user has logged in before so if any of them is true,
      // assume that they have logged in at some point.
      return (
        userTraits?.['user_account_login_created'] === true ||
        userTraits?.['has_had_user_account_login_created'] === true ||
        userTraits?.['user_account_login_created_at'] != null ||
        userTraits?.['is_logged_in'] === true
      );
    }
    return;
  }, [rudder]);

  const hasAnonymousUserLeadCaptured = useMemo(() => {
    if (rudder) {
      const userTraits = rudder.getUserTraits();

      return userTraits?.['email'] != null || userTraits?.['lead_created_at'] != null;
    }
    return;
  }, [rudder]);

  const analyticsValue = useMemo(
    () => ({
      getAnonymousId: rudder?.getAnonymousId,
      hasAnonymousUserLeadCaptured,
      hasAnonymousUserLoggedIn,
      isLoading,
      rudder,
    }),
    [hasAnonymousUserLeadCaptured, hasAnonymousUserLoggedIn, isLoading, rudder]
  );

  return <AnalyticsContext.Provider value={analyticsValue}>{children}</AnalyticsContext.Provider>;
};
